import { useEffect, useRef, useState } from 'react'
import { currentFiscalYear, getFiscalYearLabel, selectableFiscalYears } from '@budgetmonkey/shared'
import { useFiscalYearContext } from '@/lib/fiscalYear'

/**
 * Which budget year the app is showing — §11.
 *
 * This was a static label. It had to become a control the first July a
 * statement arrived carrying charges from the year that had just ended: those
 * belong to last year's budget, and with no way to look at last year they were
 * unreachable once filed there.
 *
 * A year that is not the one we are actually in is coloured, not merely
 * labelled. Every total on every screen changes when this changes, and the one
 * dangerous state is forgetting it is set — reading last June's remaining
 * balance as though it were today's is a wrong number that looks entirely
 * ordinary.
 */
export function FiscalYearMenu() {
  const { fiscalYear, setFiscalYear, isCurrent } = useFiscalYearContext()
  const [open, setOpen] = useState(false)
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Budget year ${getFiscalYearLabel(fiscalYear)}. Change year.`}
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
          isCurrent
            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            : 'bg-amber-100 text-amber-900 hover:bg-amber-200'
        }`}
      >
        {getFiscalYearLabel(fiscalYear)}
        <span aria-hidden="true" className="text-[0.9em]">
          ▾
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-8 z-50 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          {selectableFiscalYears().map((year) => (
            <button
              key={year}
              type="button"
              role="menuitem"
              onClick={() => {
                setFiscalYear(year)
                setOpen(false)
              }}
              className={`block w-full px-4 py-2.5 text-left text-sm transition hover:bg-slate-50 ${
                year === fiscalYear ? 'font-semibold text-slate-900' : 'text-slate-700'
              }`}
            >
              {getFiscalYearLabel(year)}
              {year === currentFiscalYear() && (
                <span className="ml-2 text-xs font-normal text-slate-500">current</span>
              )}
            </button>
          ))}

          {!isCurrent && (
            <p className="border-t border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-900">
              Every balance and list on screen is for{' '}
              {getFiscalYearLabel(fiscalYear)}, not this year.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
